import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaCheckCircle, FaClock, FaUsers, FaCertificate, FaChevronDown } from 'react-icons/fa';
import { getCourseBySlug } from '../data/coursesData';
import './CourseDetail.css';

const TAB_LABELS = {
  overview: 'Overview',
  features: 'What You Get',
  audience: 'For Kids & Adults',
  curriculum: 'Curriculum',
  faq: 'FAQ'
};

const CourseDetail = () => {
  const { slug } = useParams();
  const courseData = getCourseBySlug(slug);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const hasCustomTabs = Array.isArray(courseData?.customTabs) && courseData.customTabs.length > 0;

  const hasFeatures = (courseData?.features?.length > 0) || (courseData?.whatYouWillLearn?.length > 0) || (courseData?.whyChooseUs?.length > 0);
  const hasAudience = !!(courseData?.forKids || courseData?.forAdults);
  const hasCurriculum = courseData?.curriculum?.length > 0;
  const hasFaq = courseData?.faq?.length > 0;

  const defaultTabs = [
    'overview',
    ...(hasFeatures ? ['features'] : []),
    ...(hasAudience ? ['audience'] : []),
    ...(hasCurriculum ? ['curriculum'] : []),
    ...(hasFaq ? ['faq'] : [])
  ];
  const availableTabs = hasCustomTabs
    ? courseData.customTabs.map((tab) => tab.key)
    : defaultTabs;
  const tabLabelByKey = hasCustomTabs
    ? Object.fromEntries(courseData.customTabs.map((tab) => [tab.key, tab.label]))
    : TAB_LABELS;
  const defaultActiveTab = hasCustomTabs ? courseData.customTabs[0].key : 'overview';

  const overviewDescription = courseData?.fullDescription || courseData?.description || '';
  const effectiveTab = availableTabs.includes(activeTab) ? activeTab : defaultActiveTab;
  const isAccordionCustomTab =
    hasCustomTabs &&
    (effectiveTab.includes('faq') || effectiveTab.includes('question'));

  useEffect(() => {
    setActiveTab(defaultActiveTab);
  }, [slug, defaultActiveTab]);

  useEffect(() => {
    setOpenFaqIndex(null);
  }, [effectiveTab]);

  if (!courseData) {
    return (
      <div className="course-detail-page">
        <div className="page-header">
          <div className="container">
            <h1>Course Not Found</h1>
            <p>The course you are looking for does not exist.</p>
            <Link to="/courses" className="btn">Back to Courses</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="course-detail-page">
      <div className="page-header">
        <div className="container">
          <h1>{courseData.title}</h1>
          <p>{courseData.description}</p>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <div className="course-detail-grid">
            <div className="course-main-content">
              <div className="course-content-layout">
                <div
                  className="course-tabs"
                  role="tablist"
                  aria-label="Course detail sections"
                  aria-orientation="vertical"
                >
                  {availableTabs.map((tabKey) => (
                    <button
                      key={tabKey}
                      type="button"
                      role="tab"
                      id={`course-tab-${tabKey}`}
                      aria-selected={effectiveTab === tabKey}
                      aria-controls={`course-panel-${tabKey}`}
                      className={`course-tab-btn ${effectiveTab === tabKey ? 'active' : ''}`}
                      onClick={() => setActiveTab(tabKey)}
                    >
                      {tabLabelByKey[tabKey]}
                    </button>
                  ))}
                </div>

                <div className="course-tab-panels">
                {hasCustomTabs && (
                  <div
                    className="course-tab-panel"
                    role="tabpanel"
                    id={`course-panel-${effectiveTab}`}
                    aria-labelledby={`course-tab-${effectiveTab}`}
                  >
                    {isAccordionCustomTab ? (
                      <div className="content-section content-section-faq">
                        <div className="faq-accordion-list">
                          {courseData.customTabs
                            .find((tab) => tab.key === effectiveTab)
                            ?.sections?.map((section, index) => {
                              const isOpen = openFaqIndex === index;
                              return (
                                <div
                                  key={`${effectiveTab}-${index}`}
                                  className={`faq-accordion-item ${isOpen ? 'is-open' : ''}`}
                                >
                                  <button
                                    type="button"
                                    className="faq-accordion-trigger"
                                    onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                                    aria-expanded={isOpen}
                                  >
                                    <span className="faq-accordion-q">{section.heading}</span>
                                    <span className={`faq-accordion-chevron ${isOpen ? 'rotated' : ''}`}>
                                      <FaChevronDown />
                                    </span>
                                  </button>
                                  <div className={`faq-accordion-answer ${isOpen ? 'expanded' : ''}`}>
                                    {section.text && <p>{section.text}</p>}
                                    {Array.isArray(section.items) && section.items.length > 0 && (
                                      <ul className="features-list">
                                        {section.items.map((item, itemIndex) => (
                                          <li key={itemIndex}><FaCheckCircle /> {item}</li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    ) : (
                      courseData.customTabs
                        .find((tab) => tab.key === effectiveTab)
                        ?.sections?.map((section, index) => (
                          <div className="content-section" key={`${effectiveTab}-${index}`}>
                            {section.heading && <h2>{section.heading}</h2>}
                            {section.text && <p>{section.text}</p>}
                            {Array.isArray(section.items) && section.items.length > 0 && (
                              <ul className="features-list">
                                {section.items.map((item, itemIndex) => (
                                  <li key={itemIndex}><FaCheckCircle /> {item}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ))
                    )}
                  </div>
                )}

                {!hasCustomTabs && (
                  <>
                {effectiveTab === 'overview' && (
                  <div
                    className="course-tab-panel"
                    role="tabpanel"
                    id="course-panel-overview"
                    aria-labelledby="course-tab-overview"
                  >
                    {overviewDescription && (
                      <div className="content-section">
                        <h2>About This Course</h2>
                        <p>{overviewDescription}</p>
                      </div>
                    )}
                    {courseData.whyImportant && courseData.whyImportant.length > 0 && (
                      <div className="content-section">
                        <h2>Why It's Important to Learn</h2>
                        <ul className="features-list">
                          {courseData.whyImportant.map((item, index) => (
                            <li key={index}><FaCheckCircle /> {item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {effectiveTab === 'features' && hasFeatures && (
                  <div
                    className="course-tab-panel"
                    role="tabpanel"
                    id="course-panel-features"
                    aria-labelledby="course-tab-features"
                  >
                    {((courseData.whatYouWillLearn?.length > 0) || (courseData.features?.length > 0)) && (
                      <div className="content-section">
                        <h2>Course benefits</h2>
                        {(courseData.whatYouWillLearn?.length > 0) && (
                          <ul className="features-list">
                            {courseData.whatYouWillLearn.map((item, index) => (
                              <li key={index}><FaCheckCircle /> {item}</li>
                            ))}
                          </ul>
                        )}
                        {(courseData.features?.length > 0) && (
                          <ul className="features-list">
                            {courseData.features.map((feature, index) => (
                              <li key={index}><FaCheckCircle /> {feature}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                    {courseData.whyChooseUs && courseData.whyChooseUs.length > 0 && (
                      <div className="content-section why-choose-block">
                        <h2>Why choose us</h2>
                        <ul className="why-choose-list">
                          {courseData.whyChooseUs.map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {effectiveTab === 'audience' && hasAudience && (
                  <div
                    className="course-tab-panel"
                    role="tabpanel"
                    id="course-panel-audience"
                    aria-labelledby="course-tab-audience"
                  >
                    {courseData.forKids && (
                      <div className="content-section">
                        <h2>For Kids</h2>
                        <p>{courseData.forKids}</p>
                      </div>
                    )}
                    {courseData.forAdults && (
                      <div className="content-section">
                        <h2>For Adults</h2>
                        <p>{courseData.forAdults}</p>
                      </div>
                    )}
                  </div>
                )}

                {effectiveTab === 'curriculum' && hasCurriculum && (
                  <div
                    className="course-tab-panel"
                    role="tabpanel"
                    id="course-panel-curriculum"
                    aria-labelledby="course-tab-curriculum"
                  >
                    <div className="content-section">
                      <h2>Course Curriculum</h2>
                      <ul className="curriculum-list">
                        {courseData.curriculum.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {effectiveTab === 'faq' && hasFaq && (
                  <div
                    className="course-tab-panel content-section-faq"
                    role="tabpanel"
                    id="course-panel-faq"
                    aria-labelledby="course-tab-faq"
                  >
                    <h2>Frequently Asked Questions</h2>
                    <div className="faq-accordion-list">
                      {courseData.faq.map((item, index) => {
                        const isOpen = openFaqIndex === index;
                        return (
                          <div
                            key={index}
                            className={`faq-accordion-item ${isOpen ? 'is-open' : ''}`}
                          >
                            <button
                              type="button"
                              className="faq-accordion-trigger"
                              onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                              aria-expanded={isOpen}
                            >
                              <span className="faq-accordion-q">{item.question}</span>
                              <span className={`faq-accordion-chevron ${isOpen ? 'rotated' : ''}`}>
                                <FaChevronDown />
                              </span>
                            </button>
                            <div className={`faq-accordion-answer ${isOpen ? 'expanded' : ''}`}>
                              <p>{item.answer}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                </>
                )}
                </div>
              </div>
            </div>

            <div className="course-sidebar">
              <div className="sidebar-card">
                <h3>Course Information</h3>
                <ul className="course-info">
                  {courseData.duration && (
                    <li>
                      <FaClock />
                      <div>
                        <strong>Duration</strong>
                        <span>{courseData.duration}</span>
                      </div>
                    </li>
                  )}
                  {courseData.level && (
                    <li>
                      <FaCertificate />
                      <div>
                        <strong>Level</strong>
                        <span>{courseData.level}</span>
                      </div>
                    </li>
                  )}
                  {courseData.students && (
                    <li>
                      <FaUsers />
                      <div>
                        <strong>Students Enrolled</strong>
                        <span>{courseData.students}</span>
                      </div>
                    </li>
                  )}
                  <li>
                    <FaCertificate />
                    <div>
                      <strong>Certificate</strong>
                      <span>Yes</span>
                    </div>
                  </li>
                </ul>
                <Link to="/free-trial" className="btn btn-secondary">Start Free Trial</Link>
                <Link to="/contact-us" className="btn">Contact Us</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CourseDetail;
